import type { ProductSeedPath } from 'services/demosApi'

import type { ProductFormRow } from './types'

/**
 * The Shopify catalog path reports its source as the storefront's
 * `/products.json`, which is the endpoint the whole catalogue came from — not
 * any one product's page. Opening it would show an AE raw JSON.
 */
const CATALOG_ENDPOINT = /\/products\.json(\?|$)/i

/**
 * Where to send an AE who wants to look at the real listing before ticking it.
 *
 * The seeder does not return a per-product permalink, so this is derived from
 * the provenance it does return, and the two cases it produces are genuinely
 * different things:
 *
 * - The JSON-LD and paste-a-URL paths fetch a page, so their source URL IS the
 *   listing. Link straight to it.
 * - The Shopify path fetches `/products.json` and gets no permalink at all.
 *   What IS known is the platform (the response says so) and the origin, and
 *   every Shopify storefront serves `/search?q=`. So the fallback is that
 *   store's own search for this exact title, labelled as a search rather than
 *   dressed up as the listing.
 *
 * Anything else returns nothing and the control is hidden (§46) rather than
 * pointing somewhere unhelpful.
 *
 * VRG-716 — that "one field" now exists: a Shopify proposal carries `productUrl`
 * (`{origin}/products/<handle>`), which wins when present. The search fallback
 * stays for any proposal without it.
 */
export const productLinkFor = (
  sourceUrl: string,
  title: string,
  path: ProductSeedPath | undefined,
  permalink?: string | null,
): Pick<ProductFormRow, 'productUrl' | 'productUrlKind'> => {
  if (permalink) return { productUrl: permalink, productUrlKind: 'page' }
  if (!sourceUrl) return { productUrl: '', productUrlKind: 'none' }
  if (!CATALOG_ENDPOINT.test(sourceUrl)) return { productUrl: sourceUrl, productUrlKind: 'page' }
  if (path !== 'shopify' || !title.trim()) return { productUrl: '', productUrlKind: 'none' }
  try {
    const { origin } = new URL(sourceUrl)
    return {
      productUrl: `${origin}/search?q=${encodeURIComponent(title.trim())}`,
      productUrlKind: 'search',
    }
  } catch {
    return { productUrl: '', productUrlKind: 'none' }
  }
}
